import { useEffect } from 'react';
import { config } from 'config.js';
import { StompClient } from '../transport/stompClient';
import { createSyncBridge } from '../transport/syncBridge';
import { saveToLocalStorage } from '../session/localPersistence';

export function useSessionTransport(singleton) {
    useEffect(() => {
        const client = new StompClient({
            url: config.WS_URL, username: config.WS_USERNAME, password: config.WS_PASSWORD,
            autoReconnect: true, debug: false,
        });
        client.onConnect(() => { singleton.sessionStore.getState().setConnected(true); });
        client.onDisconnect(() => { singleton.sessionStore.getState().setConnected(false); });
        client.connect();
        singleton.client = client;

        // Per-session tokens. The bootstrap connection uses the static
        // credentials, which reach only form-create/form-join; once the backend
        // hands back a token scoped to this session, reconnect with it so the
        // broker -- not the UI -- is what stops an observer writing, or anyone
        // touching another session's topics.
        //
        // A backend that issues no token leaves this inert, so an older bundle
        // and an older backend both keep working.
        let appliedToken = null;
        const unsubToken = singleton.sessionStore.subscribe((s) => {
            if (s.token === appliedToken) return;
            appliedToken = s.token;
            if (!s.token) return;
            client.setCredentials({ username: '', password: s.token }, { reconnect: true });
        });

        // A rejected token is not retried: the broker would refuse it
        // identically forever. Drop back to the static credentials, which still
        // authenticate, and rejoin to mint a fresh one -- rejoining is the
        // refresh path, since it is also what extends the session's TTL.
        const unsubAuth = client.onAuthError(() => {
            const session = singleton.sessionStore.getState();
            if (!session.token) return;  // already on static credentials
            session.tokenRejected();
            appliedToken = null;
            client.setCredentials(
                { username: config.WS_USERNAME, password: config.WS_PASSWORD },
                { reconnect: true },
            );
            if (session.sessionIdPublic) {
                const rejoin = () => {
                    bridge.joinSession({
                        sessionIdPublic: session.sessionIdPublic,
                        sessionIdPrivate: session.sessionIdPrivate,
                    });
                    off();
                };
                const off = client.onConnect(rejoin);
            }
        });

        const bridge = createSyncBridge({
            flowStore: singleton.flowStore,
            flowEvents: singleton.flowEvents,
            uiStore: singleton.uiStore,
            sessionStore: singleton.sessionStore,
            client,
        });
        singleton.syncBridge = bridge;

        // Auto-join observer via ?view=
        const query = new URLSearchParams(window.location.search.substring(1));
        const viewId = query.get('view');
        let unsubSession = () => {};
        if (viewId) {
            unsubSession = singleton.sessionStore.subscribe((s) => {
                if (s.connected) { bridge.joinSession({ sessionIdPublic: viewId }); unsubSession(); }
            });
        }

        // Persist to localStorage on any itemChanged (off by default; users can
        // export the session blob or rely on the WebSocket backend instead).
        const unsubEvents = config.AUTO_PERSIST_LOCAL_STORAGE
            ? singleton.flowEvents.subscribe((ev) => {
                if (ev.type === 'itemChanged') {
                    saveToLocalStorage(singleton.flowStore.getState());
                }
            })
            : () => {};

        return () => {
            unsubToken();
            unsubAuth();
            unsubSession();
            unsubEvents();
            bridge.dispose();
            client.disconnect();
        };
        // singleton identity is stable across the app lifetime.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
}
